/**
 * Funciones tipadas del perfil del atleta (PATCH /athlete-profile).
 *
 * Sprint 14 v12 Bloque A: edición de campos que NO regeneran el plan
 * (FC máx + zapatilla). Espejo de backend/app/blueprints/athlete_profile/routes.py.
 */
import { apiFetch } from "./client";
import type { AthleteProfile, DeviceType } from "@/types/api";

/**
 * Payload parcial de PATCH /athlete-profile. Solo campos sin regeneración:
 * - `hr_max`: entero (el backend valida 40-230).
 * - `shoe`: texto o `null` para limpiar.
 * Ambos opcionales (se aplican solo los presentes).
 */
export interface UpdateProfilePayload {
  hr_max?: number;
  shoe?: string | null;
  /** Sesión 33 EXTENDIDA — Alba feature (Conversación 2 Alberto). UX-only
   *  toggle backend; NO regenera plan. Cambia solo cómo `SessionCard`
   *  renderiza el día (true → "Sesión N", false → "LUN/MAR/..."). */
  flexible_scheduling?: boolean;
}

/** PATCH /athlete-profile — edita FC máx / zapatilla. Devuelve el perfil. */
export async function updateProfile(
  payload: UpdateProfilePayload,
): Promise<AthleteProfile> {
  const data = await apiFetch<{ profile: AthleteProfile }>("/athlete-profile", {
    method: "PATCH",
    body: payload,
  });
  return data.profile;
}

/**
 * Payload parcial de PATCH /athlete-profile/training. Campos que SÍ regeneran
 * el plan. El backend valida enums + rango de días. v12 Bloque B1 introdujo
 * level/surface/days; v16.3 §3 añade `device_type` (activar/quitar reloj con
 * regen: si activas reloj y no tienes VAM medida, el motor inyecta test_vam
 * en la semana 1 del nuevo plan).
 */
export interface UpdateTrainingPayload {
  level?: "beginner" | "amateur";
  surface_primary?: "asfalto" | "trail" | "mixto";
  training_days_per_week?: number;
  device_type?: DeviceType;
}

/** Respuesta de PATCH /athlete-profile/training. */
export interface UpdateTrainingResult {
  profile: AthleteProfile;
  plan_regenerated: boolean;
  new_plan_id?: number | null;
  replaced_plan_id?: number | null;
  generation_warning?: string | null;
}

/**
 * PATCH /athlete-profile/training — edita nivel/superficie/días y REGENERA el
 * plan. Devuelve el perfil + metadatos de la regeneración (incl.
 * generation_warning si la generación lo emite).
 */
export async function updateTrainingProfile(
  payload: UpdateTrainingPayload,
): Promise<UpdateTrainingResult> {
  return apiFetch<UpdateTrainingResult>("/athlete-profile/training", {
    method: "PATCH",
    body: payload,
  });
}
